import React, { useEffect, useMemo, useState } from "react";
import { Building2, Database, KeyRound, Loader2, ShieldCheck, Users, X } from "lucide-react";
import {
  BuiltinPermissionPoints,
  DataOwnershipModeLabels,
  Locale,
  MyProjectPermissionResponse,
  ProjectCollaborationModeLabels,
  RoleScope,
  getLabelsByLocale,
} from "@packages/global";
import { Drawer, EmptyState, PageHeader, StatusPill } from "@packages/ui";
import { projectApi } from "../../services/user";

const permissionLabels = new Map(
  BuiltinPermissionPoints.filter((point) => point.scope === RoleScope.PROJECT).map((point) => [
    point.key,
    point,
  ]),
);

const moduleLabel: Record<string, string> = {
  source: "数据源",
  warehouse: "数据仓库",
  research: "研究库",
  quality: "质量控制",
  manual_correction: "人工修订",
  export: "导出",
  data_attribution: "数据归属",
  project_data: "项目数据",
  data_scope: "数据范围",
  design: "研究设计",
  query: "数据质疑",
  patient: "患者清单",
  audit: "审计",
};

const PermissionsPage: React.FC = () => {
  const [items, setItems] = useState<MyProjectPermissionResponse[]>([]);
  const [selectedItem, setSelectedItem] = useState<MyProjectPermissionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const ownershipLabels = getLabelsByLocale(DataOwnershipModeLabels, Locale.ZH_CN);
  const collaborationLabels = getLabelsByLocale(ProjectCollaborationModeLabels, Locale.ZH_CN);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    projectApi
      .listMyProjectPermissions()
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message ?? err?.message ?? "load failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        eyebrow="My Workspace"
        title="我的权限"
        description="查看我在各项目中的角色、机构范围、数据可见边界和可执行操作。"
      />

      {error && (
        <div className="rounded-xl border border-danger-border bg-danger-soft px-4 py-3 text-sm text-danger">
          加载我的权限失败：{error}
        </div>
      )}

      <div className="-mx-4 bg-bg-page px-4 sm:-mx-6 sm:px-6">
        {loading ? (
          <div className="rounded-[12px] border border-border-subtle bg-bg-surface py-16 text-center text-[13px] text-text-muted">
            <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin opacity-50" />
            加载权限中...
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-[12px] border border-border-subtle bg-bg-surface py-16">
            <EmptyState
              icon={<KeyRound className="h-8 w-8" />}
              title="暂无项目权限"
              description="你还没有被加入任何项目，或项目成员状态未启用。"
              className="border-0 bg-bg-surface py-0"
            />
          </div>
        ) : (
          <div className="overflow-hidden rounded-[12px] border border-border-subtle bg-bg-surface">
            {items.map((item, index) => (
              <article
                key={item.memberId}
                className={`flex flex-col gap-3 p-[14px_16px] transition-colors hover:bg-bg-elevated sm:flex-row sm:items-center ${
                  index > 0 ? "border-t border-border-subtle" : ""
                }`}
              >
                <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[8px] bg-primary-soft text-primary-ink">
                  <KeyRound className="h-[17px] w-[17px]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-3">
                    <h3 className="min-w-0 flex-1 truncate text-[13px] font-semibold text-text-primary">
                      {item.projectName}
                    </h3>
                    <span className="shrink-0 rounded-full bg-primary-soft px-2 py-0.5 text-[10.5px] font-semibold text-primary-ink">
                      {item.projectRoleName ?? item.projectRoleId}
                    </span>
                  </div>
                  <p className="mt-1 text-[12px] leading-[17px] text-text-secondary">
                    {formatOrgScope(item)} · {item.canViewAllProjectData ? "可查看全项目数据" : "按归属/机构范围过滤"} · {collaborationLabels[item.collaborationMode]}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedItem(item)}
                  className="inline-flex min-h-8 shrink-0 items-center justify-center rounded-[8px] border border-border-default bg-bg-surface px-3 text-[12px] font-medium text-text-secondary transition-colors hover:bg-bg-elevated"
                >
                  查看详情
                </button>
              </article>
            ))}
          </div>
        )}
      </div>

      <PermissionDetailDrawer
        item={selectedItem}
        ownershipLabel={selectedItem ? ownershipLabels[selectedItem.dataOwnershipMode] : ""}
        collaborationLabel={selectedItem ? collaborationLabels[selectedItem.collaborationMode] : ""}
        onClose={() => setSelectedItem(null)}
      />
    </div>
  );
};

function formatOrgScope(item: MyProjectPermissionResponse): string {
  if (item.organizationScope.allCenters) return "全部中心";
  const centerCount = item.organizationScope.centerIds.length;
  const departmentCount = item.organizationScope.departmentIds.length;
  if (centerCount === 0 && departmentCount === 0) return "未配置机构范围";
  return `${centerCount} 个中心 · ${departmentCount} 个科室`;
}

const PermissionGroups: React.FC<{ permissionKeys: string[] }> = ({ permissionKeys }) => {
  const groups = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const key of permissionKeys) {
      const point = permissionLabels.get(key as any);
      const module = point?.module ?? "other";
      const list = map.get(module) ?? [];
      list.push(point?.name ?? key);
      map.set(module, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [permissionKeys]);

  if (groups.length === 0) {
    return <div className="text-sm text-text-muted">暂无权限点</div>;
  }

  return (
    <div className="grid gap-2">
      {groups.map(([module, labels]) => (
        <div key={module} className="rounded-[10px] border border-border-subtle bg-bg-elevated p-3">
          <div className="text-[12px] font-semibold text-text-primary">{moduleLabel[module] ?? module}</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {labels.map((label) => (
              <StatusPill key={label} tone="neutral">
                {label}
              </StatusPill>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const InfoBlock: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string;
}> = ({ icon: Icon, title, value }) => (
  <div className="rounded-[10px] border border-border-subtle bg-bg-surface p-3">
    <div className="flex items-center gap-2 text-[11.5px] text-text-muted">
      <Icon className="h-3.5 w-3.5" />
      {title}
    </div>
    <div className="mt-1 text-[12.5px] font-medium text-text-primary">{value}</div>
  </div>
);

const PermissionDetailDrawer: React.FC<{
  item: MyProjectPermissionResponse | null;
  ownershipLabel: string;
  collaborationLabel: string;
  onClose: () => void;
}> = ({ item, ownershipLabel, collaborationLabel, onClose }) => (
  <Drawer
    isOpen={!!item}
    onClose={onClose}
    title={item?.projectName ?? "权限详情"}
    widthClassName="max-w-xl"
    contentClassName="bg-bg-surface shadow-xl"
  >
    {item && (
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-[16px] font-semibold text-text-primary">{item.projectName}</h2>
            <p className="mt-1 text-[12px] text-text-muted">项目权限详情</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[8px] p-2 text-text-secondary transition-colors hover:bg-bg-elevated"
            aria-label="关闭"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="viewport-scrollbar flex-1 space-y-4 overflow-auto bg-bg-page p-5">
          <section className="rounded-[12px] border border-border-subtle bg-bg-surface p-4">
            <div className="mb-3 text-[13px] font-semibold text-text-primary">角色与边界</div>
            <div className="flex flex-wrap gap-2">
              <StatusPill tone="brand">{item.projectRoleName ?? item.projectRoleId}</StatusPill>
              <StatusPill tone="info">{collaborationLabel}</StatusPill>
              <StatusPill tone="neutral">{ownershipLabel}</StatusPill>
            </div>
          </section>

          <section className="grid gap-3">
            <InfoBlock icon={Building2} title="机构范围" value={formatOrgScope(item)} />
            <InfoBlock
              icon={ShieldCheck}
              title="数据可见"
              value={item.canViewAllProjectData ? "可查看全项目数据" : "按归属/机构范围过滤"}
            />
            <InfoBlock icon={Users} title="待归属处理" value={item.canHandlePendingAttribution ? "可处理" : "不可处理"} />
            <InfoBlock icon={Database} title="权限点数量" value={`${item.permissionKeys.length} 个`} />
          </section>

          <section className="rounded-[12px] border border-border-subtle bg-bg-surface p-4">
            <div className="mb-3 text-[13px] font-semibold text-text-primary">权限点</div>
            <PermissionGroups permissionKeys={item.permissionKeys} />
          </section>
        </div>
      </div>
    )}
  </Drawer>
);

export default PermissionsPage;
